import { Crown, Flame } from "lucide-react";
import type { Identifier } from "ra-core";

import { cn } from "@/lib/utils";

import { sortRanking, type RankingRow } from "./callPlan";

const PLACES = [
  { height: "h-24", medal: "bg-amber-300 text-neutral-900", label: "1º" },
  { height: "h-16", medal: "bg-neutral-300 text-neutral-900", label: "2º" },
  { height: "h-12", medal: "bg-orange-300 text-neutral-900", label: "3º" },
];

/** Podium order on screen: 2nd, 1st, 3rd. */
const PODIUM_ORDER = [1, 0, 2];

const initials = (row: RankingRow) =>
  `${row.first_name?.[0] ?? ""}${row.last_name?.[0] ?? ""}`.toUpperCase();

/**
 * Today's race, big: the top three by points on a podium, and where the
 * signed-in person stands. Starts from zero every day.
 */
export const TodayPodium = ({
  ranking,
  meId,
}: {
  ranking: RankingRow[];
  meId?: Identifier;
}) => {
  const rows = sortRanking(ranking, "day");
  const top = rows.slice(0, 3);
  const meIndex = rows.findIndex((row) => String(row.id) === String(meId));
  const me = rows[meIndex];
  const nobodyYet = !rows.some((row) => row.today_points > 0);
  const ahead = meIndex > 0 ? rows[meIndex - 1] : null;

  return (
    <section className="flex flex-col gap-4 rounded-3xl bg-neutral-900 p-5 text-white dark:bg-neutral-950">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 text-lg font-semibold">
          <Flame className="h-5 w-5 text-orange-400" />
          La carrera de hoy
        </h2>
        <span className="text-sm text-neutral-300">
          {nobodyYet
            ? "Nadie ha puntuado todavía: el primero que llame se pone líder"
            : meIndex === 0
              ? `Vas 1º. Que no te pillen: ${rows[1] ? `${me.today_points - rows[1].today_points} puntos de ventaja` : "sigue sumando"}`
              : ahead && me
                ? `Vas ${meIndex + 1}º: ${ahead.today_points - me.today_points + 1} puntos para pasar a ${ahead.first_name}`
                : ""}
        </span>
      </div>

      <div className="grid grid-cols-3 items-end gap-3">
        {PODIUM_ORDER.map((place) => {
          const row = top[place];
          if (!row) return <div key={place} />;
          const isMe = String(row.id) === String(meId);
          const style = PLACES[place];
          return (
            <div key={row.id} className="flex flex-col items-center gap-1.5">
              {place === 0 && !nobodyYet ? (
                <Crown className="h-5 w-5 text-amber-300" />
              ) : null}
              <span
                className={cn(
                  "flex h-11 w-11 items-center justify-center rounded-full text-sm font-bold",
                  isMe ? "bg-lime-300 text-neutral-900" : "bg-white/15",
                )}
              >
                {initials(row)}
              </span>
              <span className="max-w-full truncate text-sm font-medium">
                {isMe ? "Tú" : row.first_name}
              </span>
              <div
                className={cn(
                  "flex w-full flex-col items-center justify-start gap-0.5 rounded-t-2xl pt-2",
                  style.height,
                  isMe ? "bg-lime-300 text-neutral-900" : "bg-white/10",
                )}
              >
                <span
                  className={cn(
                    "rounded-full px-2 text-xs font-bold",
                    style.medal,
                  )}
                >
                  {style.label}
                </span>
                <span className="text-lg font-bold leading-tight">
                  {row.today_points}
                  <span className="text-xs font-medium opacity-70"> pts</span>
                </span>
              </div>
              <span className="text-[11px] text-neutral-400">
                {row.today_calls} llamadas · {row.today_sales} ventas
              </span>
            </div>
          );
        })}
      </div>
    </section>
  );
};
